import React, { useState, useEffect, useCallback, useRef } from 'react';

/* ─── Public API — no auth required ─────────────────────────────────────── */
const API_BASE = '/api/v1';
const UPLOADS_BASE = import.meta.env.VITE_API_ORIGIN || 'http://localhost:8081';
const resolveImg = src =>
  src && typeof src === 'string' && src.startsWith('/')
    ? `${UPLOADS_BASE}${src}`
    : src;

async function pubFetch(path) {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || `HTTP ${res.status}`); }
  const j = await res.json();
  return j.data !== undefined ? j.data : j;
}

async function pubPost(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.message || `HTTP ${res.status}`);
  return j.data !== undefined ? j.data : j;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const fmt = (n, currency) => n != null
  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }).format(n)
  : '—';

const MEAL_LABELS = {
  room_only: 'Room Only', bb: 'Bed & Breakfast',
  hb: 'Half Board', fb: 'Full Board', all_inclusive: 'All Inclusive',
};

const today = () => { const d = new Date(); return d.toISOString().split('T')[0]; };
const addDays = (dateStr, n) => { const d = new Date(dateStr); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0]; };

function parseList(str) {
  if (!str) return [];
  if (Array.isArray(str)) return str;
  try { const p = JSON.parse(str); if (Array.isArray(p)) return p; } catch (_) {}
  return str.split(/[,|;\n]/).map(s => s.trim()).filter(Boolean);
}

/* ─── Design Tokens ──────────────────────────────────────────────────────── */
const C = {
  cream:       '#F9F6F1',
  ivory:       '#FDFBF8',
  gold:        '#C9922A',
  goldMid:     '#D4AF37',
  goldLight:   '#F0C060',
  goldPale:    '#FDF3DC',
  navy:        '#141E35',
  navyMid:     '#1E2E50',
  navyLight:   '#2A3F70',
  slate:       '#4A5570',
  muted:       '#8A8478',
  border:      '#E8E0D2',
  borderLight: '#F0EBE2',
  white:       '#FFFFFF',
  success:     '#1A6645',
  successPale: '#E2F4EC',
  danger:      '#B83025',
  dangerPale:  '#FDECEA',
};

const FD = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";
const FB = "'DM Sans', 'Helvetica Neue', Arial, sans-serif";
const FM = "'Courier New', monospace";

/* ─── Global Styles ──────────────────────────────────────────────────────── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: ${C.cream}; }
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(28px); } to { opacity:1; transform:none; } }
  @keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
  @keyframes slideUp { from { opacity:0; transform:translateY(48px); } to { opacity:1; transform:none; } }
  @keyframes scaleIn { from { opacity:0; transform:scale(.97); } to { opacity:1; transform:scale(1); } }
  .anim-fade-up  { animation: fadeUp .65s cubic-bezier(.16,1,.3,1) both; }
  .anim-fade-up-2 { animation: fadeUp .65s .15s cubic-bezier(.16,1,.3,1) both; }
  .anim-fade-up-3 { animation: fadeUp .65s .3s  cubic-bezier(.16,1,.3,1) both; }
  .anim-scale-in { animation: scaleIn .5s cubic-bezier(.16,1,.3,1) both; }
  input[type=date]::-webkit-calendar-picker-indicator { filter: opacity(.45) invert(0); cursor: pointer; }
  ::selection { background: ${C.goldMid}33; }
  .room-card-hover { transition: transform .3s ease, box-shadow .3s ease; }
  .room-card-hover:hover { transform: translateY(-6px); box-shadow: 0 20px 50px rgba(20,30,53,.15) !important; }
  .btn-gold {
    background: ${C.gold}; color: white; border: none; cursor: pointer;
    font-family: ${FB}; font-weight: 600; letter-spacing: .06em; text-transform: uppercase;
    transition: background .2s, transform .15s;
  }
  .btn-gold:hover { background: #B07C1C; transform: translateY(-1px); }
  .btn-ghost {
    background: transparent; border: 1.5px solid ${C.border}; cursor: pointer;
    font-family: ${FB}; color: ${C.slate}; transition: border-color .2s, color .2s;
  }
  .btn-ghost:hover { border-color: ${C.gold}; color: ${C.gold}; }
  .input-base {
    border: 1.5px solid ${C.border}; border-radius: 8px; padding: 11px 14px;
    font-size: 14px; color: ${C.navy}; font-family: ${FB};
    background: ${C.ivory}; outline: none; width: 100%;
    transition: border-color .2s;
  }
  .input-base:focus { border-color: ${C.gold}; }
  .scrollbar-hide::-webkit-scrollbar { display: none; }

  /* ── Responsive Grid Helpers ── */
  .grid-search {
    display: grid;
    grid-template-columns: 1fr 1fr 80px 80px auto;
    gap: 12px;
    align-items: end;
  }
  .grid-info-cards {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 16px;
  }
  .grid-rooms-3 {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
  .grid-detail-layout {
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 36px;
    align-items: start;
  }
  .grid-booking-layout {
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 28px;
    align-items: start;
  }
  .grid-guest-form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  .grid-specs-3 {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
  }
  .grid-amenities-2 {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  .hero-header {
    padding: 20px 48px;
  }
  .hero-header-contact {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .hero-header-contact-text {
    display: block;
  }
  .detail-gallery-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 220px 220px;
    gap: 8px;
    border-radius: 16px;
    overflow: hidden;
  }
  .footer-main {
    padding: 32px 48px;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
  }
  .results-header-inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }
  .booking-nav {
    padding: 14px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .booking-progress {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  @media (max-width: 768px) {
    .grid-search {
      grid-template-columns: 1fr 1fr;
    }
    .grid-search > *:last-child {
      grid-column: 1 / -1;
    }
    .grid-info-cards {
      grid-template-columns: 1fr;
    }
    .grid-rooms-3 {
      grid-template-columns: 1fr;
    }
    .grid-detail-layout {
      grid-template-columns: 1fr;
    }
    .grid-booking-layout {
      grid-template-columns: 1fr;
    }
    .grid-guest-form {
      grid-template-columns: 1fr;
    }
    .grid-specs-3 {
      grid-template-columns: repeat(3, 1fr);
    }
    .grid-amenities-2 {
      grid-template-columns: 1fr;
    }
    .hero-header {
      padding: 16px 20px;
    }
    .hero-header-contact-text {
      display: none;
    }
    .detail-gallery-grid {
      grid-template-columns: 1fr;
      grid-template-rows: 260px 130px 130px;
    }
    .detail-gallery-grid > *:first-child {
      grid-row: auto;
    }
    .footer-main {
      padding: 24px 20px;
      flex-direction: column;
      text-align: center;
    }
    .results-header-inner {
      flex-direction: column;
      align-items: flex-start;
    }
    .booking-nav {
      padding: 12px 16px;
    }
    .booking-progress {
      display: none;
    }
  }

  @media (max-width: 480px) {
    .grid-search {
      grid-template-columns: 1fr;
    }
    .grid-search > *:last-child {
      grid-column: 1;
    }
    .grid-specs-3 {
      grid-template-columns: 1fr;
    }
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    .grid-rooms-3 {
      grid-template-columns: repeat(2, 1fr);
    }
    .grid-detail-layout {
      grid-template-columns: 1fr 300px;
    }
    .grid-booking-layout {
      grid-template-columns: 1fr 300px;
    }
  }
`;

/* ─── Sub-components ─────────────────────────────────────────────────────── */
function Spinner({ size = 28, color }) {
  return (
    <div style={{ display: 'inline-block', width: size, height: size,
      border: `2.5px solid ${C.border}`, borderTopColor: color || C.gold,
      borderRadius: '50%', animation: 'spin .8s linear infinite', flexShrink: 0 }} />
  );
}

function StarRating({ stars, size = 16 }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} style={{ color: i < (stars || 0) ? C.goldMid : '#D8D0C0', fontSize: size, lineHeight: 1 }}>★</span>
      ))}
    </div>
  );
}

function AmenityTag({ label, icon }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: C.goldPale, border: `1px solid ${C.goldMid}25`,
      borderRadius: 6, padding: '5px 11px', fontSize: 12,
      color: C.navyMid, fontFamily: FB, fontWeight: 500,
    }}>
      {icon && <span style={{ fontSize: 13 }}>{icon}</span>}
      {label}
    </span>
  );
}

function MealBadge({ plan }) {
  const map = {
    room_only: ['🛏', 'Room Only'], bb: ['🍳', 'Bed & Breakfast'],
    hb: ['🍽', 'Half Board'], fb: ['🍽', 'Full Board'], all_inclusive: ['🌟', 'All Inclusive'],
  };
  const [icon, label] = map[plan] || ['•', plan || 'Standard'];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, background: `${C.navy}10`, borderRadius: 20,
      padding: '3px 9px', color: C.navyMid, fontFamily: FB, fontWeight: 600 }}>
      {icon} {label}
    </span>
  );
}

/* ─── Image Lightbox ─────────────────────────────────────────────────────── */
function ImageLightbox({ images, startIndex, roomName, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx(i => (i - 1 + images.length) % images.length);
  const next = () => setIdx(i => (i + 1) % images.length);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handler = e => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => { window.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, []);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(5,8,18,.97)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn .2s ease',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        padding: '18px 28px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        background: 'linear-gradient(to bottom, rgba(0,0,0,.75), transparent)',
      }}>
        <span style={{ fontFamily: FD, fontSize: 20, color: 'rgba(255,255,255,.9)', fontStyle: 'italic', fontWeight: 600 }}>
          {roomName}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,.5)', fontFamily: FB }}>
            {idx + 1} / {images.length}
          </span>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.2)',
            borderRadius: '50%', width: 38, height: 38, color: 'white',
            fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background .15s',
          }}>✕</button>
        </div>
      </div>

      <img
        src={resolveImg(images[idx])} alt={`${roomName} — photo ${idx + 1}`}
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: 'min(92vw, 1200px)', maxHeight: '78vh',
          objectFit: 'contain', borderRadius: 8,
          boxShadow: '0 40px 100px rgba(0,0,0,.8)',
        }}
        onError={e => { e.target.src = ''; }}
      />

      {images.length > 1 && (
        <>
          <button onClick={e => { e.stopPropagation(); prev(); }} style={{
            position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,.15)', borderRadius: '50%',
            width: 54, height: 54, color: 'white', fontSize: 28, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>‹</button>
          <button onClick={e => { e.stopPropagation(); next(); }} style={{
            position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,.15)', borderRadius: '50%',
            width: 54, height: 54, color: 'white', fontSize: 28, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>›</button>
        </>
      )}

      {images.length > 1 && (
        <div onClick={e => e.stopPropagation()} style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '20px 28px',
          background: 'linear-gradient(to top, rgba(0,0,0,.85), transparent)',
          display: 'flex', justifyContent: 'center', gap: 8, overflowX: 'auto',
        }}>
          {images.map((img, i) => (
            <button key={i} onClick={() => setIdx(i)} style={{
              padding: 0, border: i === idx ? `2.5px solid ${C.goldLight}` : '2px solid rgba(255,255,255,.2)',
              borderRadius: 6, cursor: 'pointer', background: 'none', flexShrink: 0,
              transition: 'border-color .2s, opacity .2s',
            }}>
              <img src={resolveImg(img)} alt="" style={{
                width: 68, height: 50, objectFit: 'cover', borderRadius: 4, display: 'block',
                opacity: i === idx ? 1 : 0.45, transition: 'opacity .2s',
              }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Image Carousel ─────────────────────────────────────────────────────── */
function ImageCarousel({ images, height = 240, roomName = '', onExpand, autoPlay = true }) {
  const [idx, setIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const intervalRef = useRef(null);
  const imgs = images && images.length > 0 ? images : [];

  useEffect(() => {
    if (imgs.length <= 1 || !autoPlay || hovered) {
      clearInterval(intervalRef.current); return;
    }
    intervalRef.current = setInterval(() => setIdx(i => (i + 1) % imgs.length), 4000);
    return () => clearInterval(intervalRef.current);
  }, [imgs.length, autoPlay, hovered]);

  const prev = e => { e.stopPropagation(); clearInterval(intervalRef.current); setIdx(i => (i - 1 + imgs.length) % imgs.length); };
  const next = e => { e.stopPropagation(); clearInterval(intervalRef.current); setIdx(i => (i + 1) % imgs.length); };

  if (imgs.length === 0) {
    return (
      <div style={{
        height, background: `linear-gradient(160deg, ${C.navyMid}, ${C.navy})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10,
      }}>
        <span style={{ fontSize: 48, opacity: .18 }}>🛏</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,.2)', fontFamily: FB }}>No photos available</span>
      </div>
    );
  }

  return (
    <div
      style={{ position: 'relative', height, overflow: 'hidden', background: C.navy, cursor: onExpand ? 'zoom-in' : 'default' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onExpand && onExpand(idx)}
    >
      {imgs.map((src, i) => (
        <img key={src} src={resolveImg(src)} alt={`${roomName} photo ${i + 1}`}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', display: 'block',
            opacity: i === idx ? 1 : 0, transition: 'opacity .6s ease', pointerEvents: 'none',
          }}
          onError={e => { e.target.style.opacity = 0; }}
        />
      ))}

      {/* Bottom vignette */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%',
        background: 'linear-gradient(to top, rgba(0,0,0,.5), transparent)', pointerEvents: 'none' }} />

      {/* Arrows */}
      {imgs.length > 1 && hovered && (
        <>
          <button onClick={prev} style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,.2)', borderRadius: '50%',
            width: 36, height: 36, cursor: 'pointer', color: 'white', fontSize: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
          }}>‹</button>
          <button onClick={next} style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,.2)', borderRadius: '50%',
            width: 36, height: 36, cursor: 'pointer', color: 'white', fontSize: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
          }}>›</button>
        </>
      )}

      {/* Dots */}
      {imgs.length > 1 && (
        <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, zIndex: 2,
          display: 'flex', justifyContent: 'center', gap: 5 }}>
          {imgs.map((_, i) => (
            <button key={i} onClick={e => { e.stopPropagation(); clearInterval(intervalRef.current); setIdx(i); }}
              style={{
                width: i === idx ? 22 : 6, height: 6, borderRadius: 3,
                border: 'none', cursor: 'pointer', padding: 0,
                background: i === idx ? C.goldLight : 'rgba(255,255,255,.4)',
                transition: 'all .3s',
              }} />
          ))}
        </div>
      )}

      {/* Photo counter */}
      <div style={{
        position: 'absolute', top: 10, right: 10, zIndex: 2,
        background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)',
        borderRadius: 20, padding: '3px 10px', fontSize: 11, color: 'rgba(255,255,255,.85)',
        fontFamily: FB,
      }}>
        📷 {idx + 1}/{imgs.length}
      </div>

      {/* Expand hint */}
      {hovered && onExpand && (
        <div style={{
          position: 'absolute', top: 10, left: 10, zIndex: 2,
          background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)',
          borderRadius: 20, padding: '3px 10px', fontSize: 10,
          color: 'rgba(255,255,255,.8)', fontFamily: FB,
        }}>⛶ View all</div>
      )}
    </div>
  );
}

/* ─── Search Widget ───────────────────────────────────────────────────────── */
function SearchWidget({ onSearch, searchLoading, initialSearch }) {
  const [arrival,   setArrival]   = useState((initialSearch?.arrival)   || addDays(today(), 1));
  const [departure, setDeparture] = useState((initialSearch?.departure) || addDays(today(), 3));
  const [adults,    setAdults]    = useState((initialSearch?.adults)    || 1);
  const [children,  setChildren]  = useState((initialSearch?.children)  || 0);
  const [error,     setError]     = useState('');

  const handleSearch = () => {
    if (!arrival || !departure) { setError('Please select check-in and check-out dates'); return; }
    if (arrival >= departure)   { setError('Check-out must be after check-in'); return; }
    setError('');
    onSearch({ arrival, departure, adults, children });
  };

  const labelStyle = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 5, display: 'block' };
  const selectStyle = { border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '11px 14px', fontSize: 14, color: C.navy, fontFamily: FB, background: C.ivory, width: '100%', outline: 'none' };

  return (
    <div style={{
      background: C.white, borderRadius: 16,
      boxShadow: '0 16px 60px rgba(20,30,53,.16), 0 4px 20px rgba(20,30,53,.08)',
      padding: '28px 28px 24px',
      border: `1px solid ${C.borderLight}`,
    }}>
      <h3 style={{ fontFamily: FD, fontSize: 22, color: C.navy, fontWeight: 700, marginBottom: 20, letterSpacing: '.01em' }}>
        Check Availability
      </h3>
      <div className="grid-search">
        <label>
          <span style={labelStyle}>Check-in</span>
          <input type="date" value={arrival} min={today()} onChange={e => setArrival(e.target.value)} className="input-base" />
        </label>
        <label>
          <span style={labelStyle}>Check-out</span>
          <input type="date" value={departure} min={addDays(arrival, 1)} onChange={e => setDeparture(e.target.value)} className="input-base" />
        </label>
        <label>
          <span style={labelStyle}>Adults</span>
          <select value={adults} onChange={e => setAdults(Number(e.target.value))} style={selectStyle}>
            {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label>
          <span style={labelStyle}>Children</span>
          <select value={children} onChange={e => setChildren(Number(e.target.value))} style={selectStyle}>
            {[0,1,2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button onClick={handleSearch} disabled={searchLoading} className="btn-gold"
          style={{ borderRadius: 10, padding: '12px 22px', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', gap: 8, height: 46 }}>
          {searchLoading ? <Spinner size={16} color="white" /> : null}
          {searchLoading ? 'Searching…' : 'Search Rooms →'}
        </button>
      </div>
      {error && <p style={{ marginTop: 10, fontSize: 13, color: C.danger, fontFamily: FB }}>{error}</p>}
    </div>
  );
}

/* ─── Room Card (for hero preview and results) ────────────────────────────── */
function RoomCard({ room, hotel, nights, onDetail, onBook, showPrice = true }) {
  const [lightbox, setLightbox] = useState(null);
  const images = parseList(room.images);
  const amenities = parseList(room.amenities);

  return (
    <div className="room-card-hover" style={{
      background: C.white, borderRadius: 14,
      border: `1px solid ${C.border}`,
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(20,30,53,.07)',
    }}>
      {lightbox && (
        <ImageLightbox images={lightbox.images} startIndex={lightbox.index} roomName={lightbox.name} onClose={() => setLightbox(null)} />
      )}

      {/* Image + Price Badge */}
      <div style={{ position: 'relative' }}>
        <ImageCarousel
          images={images} height={230} roomName={room.roomTypeName}
          onExpand={idx => setLightbox({ images, index: idx, name: room.roomTypeName })}
        />
        {showPrice && room.ratePerNight != null && (
          <div style={{
            position: 'absolute', top: 16, right: 16, zIndex: 3,
            background: C.gold, color: 'white',
            borderRadius: 8, padding: '6px 14px',
            fontFamily: FB, fontWeight: 700, fontSize: 18,
            boxShadow: '0 4px 12px rgba(201,146,42,.4)',
          }}>
            {fmt(room.ratePerNight, hotel?.currency)}
          </div>
        )}
        {room.availableRooms != null && room.availableRooms <= 3 && (
          <div style={{
            position: 'absolute', top: 16, left: 16, zIndex: 3,
            background: '#B83025', color: 'white',
            borderRadius: 20, padding: '4px 10px', fontSize: 11, fontFamily: FB, fontWeight: 600,
          }}>
            Only {room.availableRooms} left!
          </div>
        )}
      </div>

      {/* Card content */}
      <div style={{ padding: '18px 20px 20px' }}>
        <h3 style={{ fontFamily: FD, fontSize: 20, color: C.navy, fontWeight: 700, marginBottom: 8, lineHeight: 1.2 }}>
          {room.roomTypeName}
        </h3>
        <div style={{ marginBottom: 10 }}>
          <StarRating stars={5} size={14} />
        </div>

        {/* Room details row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginBottom: 12, fontSize: 12, color: C.muted, fontFamily: FB }}>
          {room.bedConfiguration && <span>🛏 {room.bedConfiguration}</span>}
          {room.size && <span>📐 {room.size}m²</span>}
          <span>👥 Up to {room.maxAdults}{room.maxChildren > 0 ? `+${room.maxChildren}` : ''}</span>
        </div>

        {/* Duration tag */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, fontSize: 12, color: C.slate, fontFamily: FB }}>
          <span style={{ opacity: .6 }}>🕐</span>
          {nights
            ? `${nights} night${nights !== 1 ? 's' : ''} · ${fmt(room.totalAmount || room.ratePerNight * nights, hotel?.currency)} total`
            : 'Best rates available — select your dates'}
        </div>

        {/* Amenities */}
        {amenities.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 16 }}>
            {amenities.slice(0, 4).map((a, i) => <AmenityTag key={i} label={a} />)}
            {amenities.length > 4 && (
              <span style={{ fontSize: 11, color: C.muted, fontFamily: FB, alignSelf: 'center', marginLeft: 2 }}>
                +{amenities.length - 4} more
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => onDetail(room)} className="btn-ghost"
            style={{ flex: 1, borderRadius: 8, padding: '10px 14px', fontSize: 12,
              fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>
            Learn More
          </button>
          <button onClick={() => onBook(room)} className="btn-gold"
            style={{ flex: 1, borderRadius: 8, padding: '10px 14px', fontSize: 12,
              letterSpacing: '.05em' }}>
            Reserve →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Hotel Hero (Step 0) ─────────────────────────────────────────────────── */
function HotelHero({ hotel, widget, previewRooms, searchLoading, onSearch, onDetail, onBook, search }) {
  const [heroIdx, setHeroIdx] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const amenities = parseList(hotel.amenities);

  // Collect hero images from all rooms
  const heroImages = previewRooms.flatMap(r => parseList(r.images).slice(0, 2));

  useEffect(() => {
    if (heroImages.length <= 1) return;
    const t = setInterval(() => setHeroIdx(i => (i + 1) % heroImages.length), 5000);
    return () => clearInterval(t);
  }, [heroImages.length]);

  const nights = search
    ? Math.round((new Date(search.departure) - new Date(search.arrival)) / 86400000)
    : null;

  return (
    <div style={{ minHeight: '100vh', background: C.cream, fontFamily: FB }}>
      {lightbox && (
        <ImageLightbox images={lightbox.images} startIndex={lightbox.index} roomName={lightbox.name} onClose={() => setLightbox(null)} />
      )}

      {/* ── HERO ── */}
      <div style={{ position: 'relative', height: '88vh', minHeight: 560, overflow: 'hidden' }}>
        {/* Background images */}
        {heroImages.length > 0 ? heroImages.map((src, i) => (
          <img key={src} src={resolveImg(src)} alt=""
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'center',
              opacity: i === heroIdx ? 1 : 0, transition: 'opacity 1.2s ease',
              pointerEvents: 'none',
            }}
            onError={e => { e.target.style.opacity = 0; }}
          />
        )) : (
          <div style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(160deg, ${C.navyLight} 0%, ${C.navy} 55%, #0B1022 100%)`,
          }} />
        )}

        {/* Dark overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,15,35,.62)' }} />

        {/* Decorative circles */}
        <div style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%',
          border: `1px solid rgba(212,175,55,.15)`, top: -150, right: -100, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', width: 350, height: 350, borderRadius: '50%',
          border: `1px solid rgba(212,175,55,.08)`, bottom: -80, left: 60, pointerEvents: 'none' }} />

        {/* ── Header inside hero ── */}
        <div className="hero-header" style={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(to bottom, rgba(10,15,35,.6), transparent)',
        }}>
          {/* Left: email */}
          <div className="hero-header-contact" style={{ minWidth: 36 }}>
            {hotel.email && (
              <>
                <div style={{ width: 36, height: 36, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>✉</div>
                <div className="hero-header-contact-text">
                  <p style={{ fontSize: 10, color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>Email Us</p>
                  <a href={`mailto:${hotel.email}`} style={{ fontSize: 12, color: 'rgba(255,255,255,.85)', textDecoration: 'none' }}>{hotel.email}</a>
                </div>
              </>
            )}
          </div>

          {/* Center: Logo / Name */}
          <div style={{ textAlign: 'center' }}>
            {widget?.logoUrl
              ? <img src={widget.logoUrl} alt={hotel.name} style={{ height: 40, objectFit: 'contain' }} />
              : (
                <>
                  <div style={{ fontFamily: FD, fontSize: 44, color: C.white, fontWeight: 700, lineHeight: .9, letterSpacing: '.02em' }}>
                    {hotel.name?.[0] || 'H'}
                  </div>
                  <div style={{ fontFamily: FB, fontSize: 10, color: 'rgba(255,255,255,.6)', letterSpacing: '.3em', textTransform: 'uppercase', marginTop: 4 }}>
                    {hotel.name?.toUpperCase()}
                  </div>
                </>
              )
            }
          </div>

          {/* Right: phone */}
          <div className="hero-header-contact" style={{ minWidth: 36, justifyContent: 'flex-end' }}>
            {hotel.phone && (
              <>
                <div className="hero-header-contact-text" style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: 10, color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600, textAlign: 'right' }}>Call Us</p>
                  <a href={`tel:${hotel.phone}`} style={{ fontSize: 12, color: 'rgba(255,255,255,.85)', textDecoration: 'none' }}>{hotel.phone}</a>
                </div>
                <div style={{ width: 36, height: 36, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>📞</div>
              </>
            )}
          </div>
        </div>

        {/* ── Hero Text ── */}
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', textAlign: 'center',
          padding: '80px 32px 120px', zIndex: 5,
        }}>
          {hotel.starRating > 0 && (
            <div className="anim-fade-up" style={{ marginBottom: 18 }}>
              <StarRating stars={hotel.starRating} size={20} />
            </div>
          )}
          <h1 className="anim-fade-up-2" style={{
            fontFamily: FD, fontSize: 'clamp(38px, 7vw, 80px)', color: C.white,
            fontWeight: 600, lineHeight: 1.1, marginBottom: 20, letterSpacing: '.01em',
            textShadow: '0 2px 30px rgba(0,0,0,.3)',
          }}>
            {hotel.welcomeMessage || 'Enjoy A Luxury Experience'}
          </h1>
          {hotel.city && (
            <p className="anim-fade-up-2" style={{ fontSize: 14, color: 'rgba(255,255,255,.65)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 32, fontWeight: 500 }}>
              📍 {[hotel.city, hotel.country].filter(Boolean).join(', ')}
            </p>
          )}
          <a href="#search" className="anim-fade-up-3 btn-gold" style={{
            padding: '16px 48px', borderRadius: 6, fontSize: 13, letterSpacing: '.15em',
            textTransform: 'uppercase', display: 'inline-block', textDecoration: 'none',
            boxShadow: `0 8px 30px ${C.gold}55`,
          }}>
            Book Now
          </a>
        </div>

        {/* Hero dot indicators */}
        {heroImages.length > 1 && (
          <div style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', gap: 6, zIndex: 5 }}>
            {heroImages.map((_, i) => (
              <button key={i} onClick={() => setHeroIdx(i)} style={{
                width: i === heroIdx ? 28 : 8, height: 8, borderRadius: 4,
                border: 'none', cursor: 'pointer', padding: 0,
                background: i === heroIdx ? C.gold : 'rgba(255,255,255,.4)',
                transition: 'all .3s',
              }} />
            ))}
          </div>
        )}
      </div>

      {/* ── Floating Search Widget ── */}
      <div id="search" style={{ maxWidth: 960, margin: '-36px auto 0', padding: '0 16px', position: 'relative', zIndex: 20 }}>
        <SearchWidget onSearch={onSearch} searchLoading={searchLoading} initialSearch={search} />
      </div>

      {/* ── Info cards row ── */}
      <div className="grid-info-cards" style={{ maxWidth: 960, margin: '32px auto 0', padding: '0 24px' }}>
        {[
          { icon: '🕐', title: 'Check-in', value: hotel.checkInTime || '14:00', sub: 'From' },
          { icon: '🕐', title: 'Check-out', value: hotel.checkOutTime || '11:00', sub: 'Until' },
          { icon: '⭐', title: 'Star Rating', value: `${hotel.starRating || 5} Stars`, sub: 'Luxury' },
        ].map(({ icon, title, value, sub }) => (
          <div key={title} style={{
            background: C.white, borderRadius: 12, padding: '18px 22px',
            border: `1px solid ${C.borderLight}`,
            display: 'flex', alignItems: 'center', gap: 16,
            boxShadow: '0 2px 12px rgba(20,30,53,.05)',
          }}>
            <div style={{ width: 46, height: 46, borderRadius: '50%', background: C.goldPale,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
              {icon}
            </div>
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.08em' }}>{title}</p>
              <p style={{ fontFamily: FD, fontSize: 22, color: C.navy, fontWeight: 700, lineHeight: 1.1, marginTop: 2 }}>{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Amenities ── */}
      {amenities.length > 0 && (
        <div style={{ maxWidth: 960, margin: '28px auto 0', padding: '0 24px' }}>
          <div style={{ background: C.white, borderRadius: 12, padding: '22px 24px', border: `1px solid ${C.borderLight}` }}>
            <h3 style={{ fontFamily: FD, fontSize: 20, color: C.navy, fontWeight: 700, marginBottom: 14 }}>Hotel Amenities</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {amenities.map((a, i) => <AmenityTag key={i} label={a} />)}
            </div>
          </div>
        </div>
      )}

      {/* ── Our Rooms Section ── */}
      <div style={{ maxWidth: 960, margin: '52px auto 0', padding: '0 24px 80px' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.gold, textTransform: 'uppercase', letterSpacing: '.2em', fontFamily: FB, marginBottom: 10 }}>
            Accommodation
          </p>
          <h2 style={{ fontFamily: FD, fontSize: 'clamp(28px,4vw,44px)', color: C.navy, fontWeight: 700, marginBottom: 12 }}>
            Our Most Popular Rooms
          </h2>
          <div style={{ width: 60, height: 2, background: C.gold, margin: '0 auto 14px' }} />
          <p style={{ color: C.muted, fontSize: 14, fontFamily: FB, maxWidth: 480, margin: '0 auto' }}>
            {previewRooms.length > 0
              ? 'Select dates above to see live pricing and availability'
              : 'Search dates above to explore our available rooms and rates'}
          </p>
        </div>

        {previewRooms.length > 0 ? (
          <div className="grid-rooms-3">
            {previewRooms.map(room => (
              <RoomCard
                key={room.roomTypeId}
                room={room}
                hotel={hotel}
                nights={nights}
                onDetail={onDetail}
                onBook={onBook}
                showPrice={room.ratePerNight != null}
              />
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: C.muted }}>
            <div style={{ fontSize: 48, marginBottom: 16, opacity: .3 }}>🛏</div>
            <p style={{ fontSize: 15, fontFamily: FB }}>Enter your dates above to explore our rooms</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="footer-main" style={{ background: C.navy }}>
        <div>
          <p style={{ fontFamily: FD, fontSize: 20, color: C.white, fontWeight: 600 }}>{hotel.name}</p>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', fontFamily: FB, marginTop: 4 }}>
            {[hotel.city, hotel.country].filter(Boolean).join(', ')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
          {hotel.email && <a href={`mailto:${hotel.email}`} style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', textDecoration: 'none', fontFamily: FB }}>✉ {hotel.email}</a>}
          {hotel.phone && <a href={`tel:${hotel.phone}`} style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', textDecoration: 'none', fontFamily: FB }}>📞 {hotel.phone}</a>}
        </div>
      </div>
    </div>
  );
}

/* ─── Room Detail View ─────────────────────────────────────────────────────── */
function RoomDetailView({ room, hotel, search, onBook, onBack }) {
  const [lightbox, setLightbox] = useState(null);
  const images = parseList(room.images);
  const amenities = parseList(room.amenities);
  const plans = room.ratePlans || [];
  const nights = search ? Math.round((new Date(search.departure) - new Date(search.arrival)) / 86400000) : null;

  const AMENITY_ICONS = {
    'WiFi': '📶', 'wifi': '📶', 'Air Conditioning': '❄️', 'TV': '📺', 'Minibar': '🍾',
    'Safe': '🔒', 'Balcony': '🌅', 'View': '🌄', 'Bathroom': '🚿', 'Coffee': '☕',
    'Bath': '🛁', 'Gym': '🏋️', 'Pool': '🏊', 'Spa': '💆', 'Restaurant': '🍽',
    'Bar': '🍸', 'Parking': '🅿️',
  };

  const getIcon = (amenity) => {
    for (const [key, icon] of Object.entries(AMENITY_ICONS)) {
      if (amenity.toLowerCase().includes(key.toLowerCase())) return icon;
    }
    return '✓';
  };

  return (
    <div style={{ minHeight: '100vh', background: C.cream, fontFamily: FB }}>
      {lightbox && (
        <ImageLightbox images={lightbox.images} startIndex={lightbox.index} roomName={lightbox.name} onClose={() => setLightbox(null)} />
      )}

      {/* Back navigation */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 14, color: C.slate, fontFamily: FB, display: 'flex', alignItems: 'center', gap: 6 }}>
          ← Back
        </button>
        <div style={{ fontFamily: FD, fontSize: 17, color: C.navy, fontWeight: 600 }}>{hotel.name}</div>
        <div style={{ width: 80 }} />
      </div>

      {/* Image gallery grid */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px 0' }}>
        {images.length === 0 ? (
          <div style={{ height: 400, background: `linear-gradient(160deg, ${C.navyMid}, ${C.navy})`,
            borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 64, opacity: .15 }}>🛏</span>
          </div>
        ) : images.length === 1 ? (
          <div style={{ borderRadius: 16, overflow: 'hidden', height: 440, cursor: 'zoom-in' }}
            onClick={() => setLightbox({ images, index: 0, name: room.roomTypeName })}>
            <img src={resolveImg(images[0])} alt={room.roomTypeName}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        ) : (
          <div className="detail-gallery-grid">
            {/* Main image */}
            <div style={{ gridRow: '1 / 3', cursor: 'zoom-in', overflow: 'hidden' }}
              onClick={() => setLightbox({ images, index: 0, name: room.roomTypeName })}>
              <img src={resolveImg(images[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform .3s' }}
                onMouseOver={e => e.currentTarget.style.transform = 'scale(1.03)'}
                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'} />
            </div>
            {/* Side images */}
            {[images[1], images[2] || images[1]].map((src, i) => (
              <div key={i} style={{ cursor: 'zoom-in', overflow: 'hidden', position: 'relative' }}
                onClick={() => setLightbox({ images, index: i + 1, name: room.roomTypeName })}>
                <img src={resolveImg(src)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform .3s' }}
                  onMouseOver={e => e.currentTarget.style.transform = 'scale(1.03)'}
                  onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'} />
                {i === 1 && images.length > 3 && (
                  <div onClick={e => { e.stopPropagation(); setLightbox({ images, index: 0, name: room.roomTypeName }); }}
                    style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <span style={{ color: C.white, fontFamily: FB, fontWeight: 700, fontSize: 15 }}>
                      +{images.length - 3} photos
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="grid-detail-layout" style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 80px' }}>

        {/* Left: Room info */}
        <div>
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
              <h1 style={{ fontFamily: FD, fontSize: 'clamp(26px,3vw,38px)', color: C.navy, fontWeight: 700, lineHeight: 1.1 }}>
                {room.roomTypeName}
              </h1>
              {room.ratePerNight != null && (
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontFamily: FD, fontSize: 34, color: C.gold, fontWeight: 700, lineHeight: 1 }}>
                    {fmt(room.ratePerNight, hotel.currency)}
                  </p>
                  <p style={{ fontSize: 12, color: C.muted }}>per night</p>
                </div>
              )}
            </div>
            <StarRating stars={5} size={16} />
          </div>

          {/* Room specs */}
          <div className="grid-specs-3" style={{ marginBottom: 30 }}>
            {[
              { icon: '🛏', label: 'Bed Type', value: room.bedConfiguration || '—' },
              { icon: '📐', label: 'Room Size', value: room.size ? `${room.size} m²` : '—' },
              { icon: '👥', label: 'Capacity', value: `${room.maxAdults || 2} Adult${(room.maxAdults || 2) > 1 ? 's' : ''}${room.maxChildren > 0 ? ` + ${room.maxChildren} Child${room.maxChildren > 1 ? 'ren' : ''}` : ''}` },
            ].map(({ icon, label, value }) => (
              <div key={label} style={{ background: C.white, borderRadius: 10, padding: '16px 18px', border: `1px solid ${C.borderLight}`, textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{icon}</div>
                <p style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>{label}</p>
                <p style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{value}</p>
              </div>
            ))}
          </div>

          {/* Description */}
          {room.description && (
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ fontFamily: FD, fontSize: 22, color: C.navy, fontWeight: 700, marginBottom: 12 }}>About This Room</h3>
              <div style={{ width: 40, height: 2, background: C.gold, marginBottom: 16 }} />
              <p style={{ fontSize: 15, color: C.slate, lineHeight: 1.8 }}>{room.description}</p>
            </div>
          )}

          {/* Amenities */}
          {amenities.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ fontFamily: FD, fontSize: 22, color: C.navy, fontWeight: 700, marginBottom: 12 }}>Room Amenities</h3>
              <div style={{ width: 40, height: 2, background: C.gold, marginBottom: 20 }} />
              <div className="grid-amenities-2">
                {amenities.map((a, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: C.slate, fontFamily: FB }}>
                    <span style={{ width: 28, height: 28, borderRadius: '50%', background: C.goldPale,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                      {getIcon(a)}
                    </span>
                    {a}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rate Plans */}
          {plans.length > 0 && (
            <div>
              <h3 style={{ fontFamily: FD, fontSize: 22, color: C.navy, fontWeight: 700, marginBottom: 12 }}>Rate Plans</h3>
              <div style={{ width: 40, height: 2, background: C.gold, marginBottom: 20 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {plans.map(plan => (
                  <div key={plan.id} style={{ background: C.white, borderRadius: 12, padding: '16px 20px',
                    border: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 14, color: C.navy, marginBottom: 4 }}>{plan.name}</p>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <MealBadge plan={plan.mealPlan} />
                        {plan.minStay > 1 && <span style={{ fontSize: 11, color: C.muted }}>Min {plan.minStay} nights</span>}
                        {plan.cancellationPolicy && <span style={{ fontSize: 11, color: C.muted }}>{plan.cancellationPolicy}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontFamily: FD, fontSize: 22, color: C.gold, fontWeight: 700 }}>{fmt(plan.amount, hotel.currency)}</p>
                      <p style={{ fontSize: 11, color: C.muted }}>per night</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Booking summary card */}
        <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`,
          overflow: 'hidden', position: 'sticky', top: 20,
          boxShadow: '0 8px 40px rgba(20,30,53,.1)' }}>
          {images.length > 0 && (
            <div style={{ height: 180, overflow: 'hidden' }}>
              <img src={resolveImg(images[0])} alt={room.roomTypeName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}
          <div style={{ padding: '24px' }}>
            <h3 style={{ fontFamily: FD, fontSize: 20, color: C.navy, fontWeight: 700, marginBottom: 16 }}>
              Your Stay
            </h3>

            {search ? (
              <>
                {[
                  ['Check-in',  search.arrival],
                  ['Check-out', search.departure],
                  ['Duration',  `${nights} night${nights !== 1 ? 's' : ''}`],
                  ['Guests',    `${search.adults} adult${search.adults > 1 ? 's' : ''}${search.children > 0 ? ` + ${search.children}` : ''}`],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 10 }}>
                    <span style={{ color: C.muted }}>{k}</span>
                    <span style={{ fontWeight: 600, color: C.navy }}>{v}</span>
                  </div>
                ))}
                {room.ratePerNight != null && (
                  <div style={{ borderTop: `2px solid ${C.border}`, marginTop: 14, paddingTop: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, color: C.muted }}>{fmt(room.ratePerNight, hotel.currency)} × {nights} nights</span>
                      <span style={{ fontFamily: FD, fontSize: 26, color: C.gold, fontWeight: 700 }}>
                        {fmt(room.totalAmount || room.ratePerNight * nights, hotel.currency)}
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
                Search for available dates to see pricing for this room.
              </p>
            )}

            <button onClick={() => onBook(room)} className="btn-gold"
              style={{ width: '100%', borderRadius: 10, padding: '14px', fontSize: 14,
                letterSpacing: '.08em', marginTop: 20, display: 'block' }}>
              {search ? 'Reserve This Room →' : 'Check Availability →'}
            </button>

            {hotel.checkInTime && (
              <div style={{ marginTop: 16, background: C.cream, borderRadius: 8, padding: '10px 12px', fontSize: 12, color: C.slate }}>
                <p>🕐 Check-in from <strong>{hotel.checkInTime}</strong></p>
                <p style={{ marginTop: 4 }}>🕐 Check-out by <strong>{hotel.checkOutTime || '11:00'}</strong></p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Search Results (Step 1) ─────────────────────────────────────────────── */
function SearchResults({ results, hotel, search, onDetail, onBook, onBack }) {
  const nights = Math.round((new Date(search.departure) - new Date(search.arrival)) / 86400000);

  if (results.length === 0) return (
    <div style={{ maxWidth: 600, margin: '100px auto', textAlign: 'center', padding: '0 24px', fontFamily: FB }}>
      <div style={{ fontSize: 64, marginBottom: 20, opacity: .25 }}>🛏</div>
      <h2 style={{ fontFamily: FD, fontSize: 30, color: C.navy, marginBottom: 12 }}>No Rooms Available</h2>
      <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.7, marginBottom: 32 }}>
        No rooms match your search for <strong>{search.adults} guest{search.adults > 1 ? 's' : ''}</strong>{' '}
        from <strong>{search.arrival}</strong> to <strong>{search.departure}</strong>.
        Please try different dates.
      </p>
      <button onClick={onBack} className="btn-gold" style={{ borderRadius: 10, padding: '13px 32px', fontSize: 14 }}>
        ← Modify Search
      </button>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: C.cream, fontFamily: FB }}>
      {/* Results header */}
      <div style={{ background: C.navy, padding: '28px 20px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div className="results-header-inner">
            <div>
              <h2 style={{ fontFamily: FD, fontSize: 26, color: C.white, fontWeight: 700 }}>
                {results.length} Room Type{results.length !== 1 ? 's' : ''} Available
              </h2>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,.55)', fontFamily: FB, marginTop: 4 }}>
                {search.arrival} → {search.departure} · {nights} night{nights !== 1 ? 's' : ''} ·{' '}
                {search.adults} adult{search.adults > 1 ? 's' : ''}
                {search.children > 0 ? `, ${search.children} child${search.children > 1 ? 'ren' : ''}` : ''}
              </p>
            </div>
            <button onClick={onBack} className="btn-ghost"
              style={{ borderRadius: 8, padding: '10px 20px', fontSize: 13,
                border: '1.5px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.7)' }}>
              ← Modify Search
            </button>
          </div>
        </div>
      </div>

      {/* Modify search */}
      <div style={{ maxWidth: 960, margin: '-20px auto 0', padding: '0 24px', position: 'relative', zIndex: 10 }}>
        <SearchWidget onSearch={(params) => { onBack(); /* Will trigger new search from parent */ }} initialSearch={search} />
      </div>

      {/* Room grid */}
      <div style={{ maxWidth: 960, margin: '36px auto 80px', padding: '0 24px' }}>
        <div className="grid-rooms-3">
          {results.map(room => (
            <RoomCard
              key={room.roomTypeId}
              room={room}
              hotel={hotel}
              nights={nights}
              onDetail={onDetail}
              onBook={onBook}
              showPrice
            />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{ background: C.navy, padding: '24px 20px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <span style={{ fontFamily: FD, fontSize: 16, color: 'rgba(255,255,255,.7)', fontWeight: 600 }}>{hotel.name}</span>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,.35)', fontFamily: FB }}>
          {[hotel.city, hotel.country].filter(Boolean).join(', ')}
        </span>
      </div>
    </div>
  );
}

/* ─── Booking Form (Step 2) ───────────────────────────────────────────────── */
function BookingForm({ room, hotel, search, onConfirm, onBack, loading }) {
  const [form, setForm] = useState({
    guestName: '', guestEmail: '', guestPhone: '', nationality: '',
    specialRequests: '', paymentMethod: 'cash_on_arrival',
    selectedRatePlan: room.ratePlans?.[0]?.id || null,
  });
  const [errors, setErrors] = useState({});
  const [lightbox, setLightbox] = useState(null);

  const nights = Math.round((new Date(search.departure) - new Date(search.arrival)) / 86400000);
  const selectedPlan = room.ratePlans?.find(p => p.id === form.selectedRatePlan);
  const ratePerNight = selectedPlan?.amount ?? room.ratePerNight;
  const totalAmount  = ratePerNight != null ? ratePerNight * nights : null;
  const images = parseList(room.images);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const validate = () => {
    const e = {};
    if (!form.guestName.trim())  e.guestName  = 'Full name is required';
    if (!form.guestEmail.trim()) e.guestEmail = 'Email is required';
    if (form.guestEmail && !/\S+@\S+\.\S+/.test(form.guestEmail)) e.guestEmail = 'Invalid email';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onConfirm({
      ...form,
      roomTypeId: room.roomTypeId,
      arrivalDate: search.arrival,
      departureDate: search.departure,
      adults: search.adults,
      children: search.children,
      ratePlanId: form.selectedRatePlan,
    });
  };

  const LABEL = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };

  return (
    <>
      {lightbox && <ImageLightbox images={lightbox.images} startIndex={lightbox.index} roomName={lightbox.name} onClose={() => setLightbox(null)} />}

      <div style={{ minHeight: '100vh', background: C.cream, fontFamily: FB }}>
        {/* Top nav */}
        <div className="booking-nav" style={{ background: C.white, borderBottom: `1px solid ${C.border}` }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 14, color: C.slate, fontFamily: FB, display: 'flex', alignItems: 'center', gap: 6 }}>
            ← Back to Rooms
          </button>
          <div style={{ fontFamily: FD, fontSize: 17, color: C.navy, fontWeight: 600 }}>{hotel.name}</div>
          {/* Progress */}
          <div className="booking-progress">
            {['Rooms', 'Details', 'Confirm'].map((label, i) => (
              <React.Fragment key={label}>
                <span style={{ fontSize: 12, fontWeight: i === 1 ? 700 : 400, color: i <= 1 ? C.gold : C.muted, fontFamily: FB }}>
                  {label}
                </span>
                {i < 2 && <span style={{ color: C.border, fontSize: 12 }}>›</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div style={{ maxWidth: 960, margin: '0 auto', padding: '36px 24px 80px' }}>
          <div className="grid-booking-layout">

            {/* Form */}
            <div>
              <h2 style={{ fontFamily: FD, fontSize: 32, color: C.navy, fontWeight: 700, marginBottom: 8 }}>
                Complete Your Booking
              </h2>
              <div style={{ width: 50, height: 2, background: C.gold, marginBottom: 28 }} />

              {/* Rate plan selector */}
              {room.ratePlans?.length > 0 && (
                <div style={{ background: C.white, borderRadius: 14, padding: 24, border: `1px solid ${C.border}`, marginBottom: 20 }}>
                  <h3 style={{ fontFamily: FD, fontSize: 18, color: C.navy, marginBottom: 16, fontWeight: 700 }}>Select Rate Plan</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {room.ratePlans.map(plan => (
                      <label key={plan.id} style={{
                        display: 'flex', gap: 14, alignItems: 'center',
                        background: form.selectedRatePlan === plan.id ? C.goldPale : C.cream,
                        border: `2px solid ${form.selectedRatePlan === plan.id ? C.gold : C.border}`,
                        borderRadius: 10, padding: '14px 16px', cursor: 'pointer', transition: 'all .2s',
                      }}>
                        <input type="radio" name="ratePlan" value={plan.id}
                          checked={form.selectedRatePlan === plan.id}
                          onChange={() => set('selectedRatePlan', plan.id)}
                          style={{ accentColor: C.gold, width: 16, height: 16 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                            <span style={{ fontWeight: 700, fontSize: 14, color: C.navy }}>{plan.name}</span>
                            <span style={{ fontFamily: FD, fontSize: 18, color: C.gold, fontWeight: 700 }}>
                              {fmt(plan.amount, hotel.currency)}/night
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <MealBadge plan={plan.mealPlan} />
                            {plan.cancellationPolicy && <span style={{ fontSize: 11, color: C.muted }}>{plan.cancellationPolicy}</span>}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Guest info */}
              <div style={{ background: C.white, borderRadius: 14, padding: 24, border: `1px solid ${C.border}`, marginBottom: 20 }}>
                <h3 style={{ fontFamily: FD, fontSize: 18, color: C.navy, marginBottom: 18, fontWeight: 700 }}>Guest Information</h3>
                <div className="grid-guest-form">
                  {[
                    { label: 'Full Name *', key: 'guestName', type: 'text', placeholder: 'Your full name' },
                    { label: 'Email Address *', key: 'guestEmail', type: 'email', placeholder: 'your@email.com' },
                    { label: 'Phone Number', key: 'guestPhone', type: 'tel', placeholder: '+250 7xx xxx xxx' },
                    { label: 'Nationality', key: 'nationality', type: 'text', placeholder: 'e.g. Rwandan' },
                  ].map(({ label, key, type, placeholder }) => (
                    <label key={key}>
                      <span style={{ ...LABEL, color: errors[key] ? C.danger : C.muted }}>{label}</span>
                      <input type={type} value={form[key]} placeholder={placeholder}
                        onChange={e => set(key, e.target.value)} className="input-base"
                        style={{ border: `1.5px solid ${errors[key] ? C.danger : C.border}`, borderRadius: 8,
                          padding: '11px 14px', fontSize: 14, color: C.navy, fontFamily: FB, background: C.ivory, outline: 'none', width: '100%' }} />
                      {errors[key] && <span style={{ fontSize: 11, color: C.danger, marginTop: 3, display: 'block' }}>{errors[key]}</span>}
                    </label>
                  ))}
                </div>
                <div style={{ marginTop: 16 }}>
                  <span style={LABEL}>Special Requests</span>
                  <textarea value={form.specialRequests} onChange={e => set('specialRequests', e.target.value)}
                    placeholder="Early check-in, dietary requirements, bed preferences, allergies…"
                    rows={3}
                    style={{ border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '11px 14px',
                      fontSize: 13, color: C.navy, fontFamily: FB, background: C.ivory,
                      resize: 'vertical', outline: 'none', width: '100%' }} />
                </div>
              </div>

              {/* Payment */}
              <div style={{ background: C.white, borderRadius: 14, padding: 24, border: `1px solid ${C.border}`, marginBottom: 28 }}>
                <h3 style={{ fontFamily: FD, fontSize: 18, color: C.navy, marginBottom: 18, fontWeight: 700 }}>Payment Method</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { id: 'cash_on_arrival', label: '💵 Pay Cash at Arrival', desc: 'Pay in cash when you check in. No card required.' },
                    { id: 'mobile_money',    label: '📱 Mobile Money at Arrival', desc: 'MTN MoMo / Airtel Money — pay at reception upon arrival.' },
                    { id: 'bank_transfer',   label: '🏦 Bank Transfer', desc: 'Transfer to our account. Booking confirmed on payment receipt.' },
                    { id: 'online_card',     label: '💳 Credit / Debit Card', desc: 'Secure online card payment. Contact hotel for details.' },
                  ].map(opt => (
                    <label key={opt.id} style={{
                      display: 'flex', gap: 14, alignItems: 'flex-start',
                      background: form.paymentMethod === opt.id ? C.goldPale : C.cream,
                      border: `2px solid ${form.paymentMethod === opt.id ? C.gold : C.border}`,
                      borderRadius: 10, padding: '14px 16px', cursor: 'pointer', transition: 'all .2s',
                    }}>
                      <input type="radio" name="payment" value={opt.id}
                        checked={form.paymentMethod === opt.id}
                        onChange={() => set('paymentMethod', opt.id)}
                        style={{ accentColor: C.gold, marginTop: 2, width: 16, height: 16 }} />
                      <div>
                        <p style={{ fontWeight: 700, fontSize: 13, color: C.navy, marginBottom: 3 }}>{opt.label}</p>
                        <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <button onClick={handleSubmit} disabled={loading} className="btn-gold"
                style={{ width: '100%', borderRadius: 12, padding: '16px', fontSize: 15,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  opacity: loading ? .7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? <><Spinner size={18} color="white" /> Confirming Reservation…</> : `Confirm Booking — ${fmt(totalAmount, hotel.currency)}`}
              </button>
            </div>

            {/* Summary sidebar */}
            <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`,
              overflow: 'hidden', position: 'sticky', top: 20,
              boxShadow: '0 8px 40px rgba(20,30,53,.1)' }}>
              {images.length > 0 && (
                <div style={{ height: 190, overflow: 'hidden', cursor: 'zoom-in' }}
                  onClick={() => setLightbox({ images, index: 0, name: room.roomTypeName })}>
                  <img src={resolveImg(images[0])} alt={room.roomTypeName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              )}
              <div style={{ padding: '22px 24px 26px' }}>
                <h3 style={{ fontFamily: FD, fontSize: 19, color: C.navy, fontWeight: 700, marginBottom: 4 }}>{room.roomTypeName}</h3>
                <p style={{ fontSize: 12, color: C.muted, marginBottom: 18 }}>{hotel.name}</p>
                <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${C.borderLight}` }}>
                  <StarRating stars={hotel.starRating} size={13} />
                </div>
                {[
                  ['Check-in',  search.arrival],
                  ['Check-out', search.departure],
                  ['Duration',  `${nights} night${nights !== 1 ? 's' : ''}`],
                  ['Guests',    `${search.adults} adult${search.adults > 1 ? 's' : ''}${search.children > 0 ? ` + ${search.children}` : ''}`],
                  ['Rate Plan', selectedPlan?.name || 'Standard'],
                  ['Board',     MEAL_LABELS[selectedPlan?.mealPlan || 'room_only']],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 9, color: C.slate }}>
                    <span style={{ color: C.muted }}>{k}</span>
                    <span style={{ fontWeight: 600, textAlign: 'right', maxWidth: '55%', fontSize: 12 }}>{v}</span>
                  </div>
                ))}
                <div style={{ borderTop: `2px solid ${C.border}`, marginTop: 14, paddingTop: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: C.muted }}>{fmt(ratePerNight, hotel.currency)} × {nights}n</span>
                    <span style={{ fontFamily: FD, fontSize: 26, color: C.gold, fontWeight: 700 }}>{fmt(totalAmount, hotel.currency)}</span>
                  </div>
                  <p style={{ fontSize: 10, color: C.muted, marginTop: 4, textAlign: 'right' }}>{hotel.currency} · taxes may apply</p>
                </div>
                {hotel.checkInTime && (
                  <div style={{ marginTop: 16, background: C.cream, borderRadius: 8, padding: '10px 12px', fontSize: 12, color: C.slate, lineHeight: 1.7 }}>
                    <p>🕐 Check-in from <strong>{hotel.checkInTime}</strong></p>
                    <p>🕐 Check-out by <strong>{hotel.checkOutTime || '11:00'}</strong></p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Confirmation (Step 3) ───────────────────────────────────────────────── */
function ConfirmationScreen({ booking, hotel, onReset }) {
  const PAYMENT_INST = {
    cash_on_arrival: 'Please bring the full amount in cash upon arrival. Our reception team will be happy to assist.',
    mobile_money:    'Please pay via MTN MoMo or Airtel Money at reception on the day of your arrival.',
    bank_transfer:   `Please transfer the full amount to our hotel bank account, quoting reference: ${booking.confirmationNumber}. Booking confirmed on receipt.`,
    online_card:     'Please contact the hotel directly to complete your card payment and finalize your reservation.',
  };

  return (
    <div style={{ minHeight: '100vh', background: C.cream, fontFamily: FB, display: 'flex', flexDirection: 'column' }}>
      {/* Top accent bar */}
      <div style={{ height: 4, background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight}, ${C.gold})` }} />

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '60px 24px 80px', flex: 1 }}>
        {/* Success icon */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 90, height: 90, background: C.successPale, borderRadius: '50%',
            margin: '0 auto 24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 40, boxShadow: `0 0 0 12px ${C.successPale}80`,
          }}>✅</div>
          <h1 style={{ fontFamily: FD, fontSize: 38, color: C.navy, fontWeight: 700, marginBottom: 10 }}>
            Booking {booking.instantConfirm ? 'Confirmed!' : 'Received!'}
          </h1>
          <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.8, maxWidth: 440, margin: '0 auto' }}>
            {booking.instantConfirm
              ? 'Your reservation is confirmed. A confirmation has been sent to your email address.'
              : 'Your booking request has been received. Our team will confirm within 24 hours.'}
          </p>
        </div>

        {/* Confirmation card */}
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 18,
          overflow: 'hidden', marginBottom: 20, boxShadow: '0 8px 40px rgba(20,30,53,.08)' }}>
          {/* Gold header */}
          <div style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.goldMid})`, padding: '20px 28px' }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.7)', textTransform: 'uppercase', letterSpacing: '.12em', marginBottom: 4 }}>Confirmation Number</p>
            <p style={{ fontFamily: FM, fontSize: 28, color: C.white, fontWeight: 700, letterSpacing: 3 }}>
              {booking.confirmationNumber}
            </p>
          </div>

          {/* Status badge */}
          <div style={{ padding: '20px 28px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: C.muted }}>Reservation Status</span>
            <span style={{
              background: booking.status === 'confirmed' ? C.successPale : C.goldPale,
              color: booking.status === 'confirmed' ? C.success : C.gold,
              borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em',
            }}>
              {booking.status}
            </span>
          </div>

          {/* Details */}
          <div style={{ padding: '20px 28px' }}>
            {[
              ['Hotel',     booking.hotelName],
              ['Room',      booking.roomTypeName],
              ['Board',     MEAL_LABELS[booking.mealPlan] || booking.mealPlan],
              ['Guest',     booking.guestName],
              ['Email',     booking.guestEmail],
              ['Check-in',  `${booking.arrivalDate} · from ${booking.checkInTime || '14:00'}`],
              ['Check-out', `${booking.departureDate} · by ${booking.checkOutTime || '11:00'}`],
              ['Duration',  `${booking.numNights} night${booking.numNights !== 1 ? 's' : ''}`],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${C.borderLight}` }}>
                <span style={{ color: C.muted, minWidth: 90 }}>{k}</span>
                <span style={{ fontWeight: 600, color: C.navy, textAlign: 'right', maxWidth: '60%' }}>{v}</span>
              </div>
            ))}
            {/* Total */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: C.navy }}>Total Amount</span>
              <span style={{ fontFamily: FD, fontSize: 30, color: C.gold, fontWeight: 700 }}>
                {fmt(booking.totalAmount, booking.currency)}
              </span>
            </div>
          </div>
        </div>

        {/* Payment instructions */}
        {booking.paymentMethod && (
          <div style={{ background: C.goldPale, border: `1px solid ${C.gold}30`, borderRadius: 12, padding: '18px 22px', marginBottom: 20 }}>
            <p style={{ fontWeight: 700, fontSize: 13, color: C.navy, marginBottom: 8 }}>💳 Payment Instructions</p>
            <p style={{ fontSize: 13, color: C.slate, lineHeight: 1.7 }}>
              {PAYMENT_INST[booking.paymentMethod] || PAYMENT_INST.cash_on_arrival}
            </p>
          </div>
        )}

        {/* Cancellation */}
        {booking.cancellationPolicy && (
          <div style={{ background: C.cream, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', fontSize: 12, color: C.muted, marginBottom: 28, lineHeight: 1.7 }}>
            <strong>Cancellation Policy:</strong> {booking.cancellationPolicy}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 32 }}>
          {booking.hotelEmail && (
            <a href={`mailto:${booking.hotelEmail}`}
              style={{ flex: 1, background: C.navy, color: C.white, borderRadius: 10, padding: '12px 20px',
                fontSize: 13, fontWeight: 700, textDecoration: 'none', textAlign: 'center',
                fontFamily: FB, minWidth: 140 }}>
              ✉ Email Hotel
            </a>
          )}
          {booking.hotelPhone && (
            <a href={`tel:${booking.hotelPhone}`}
              style={{ flex: 1, background: C.white, color: C.navy, border: `1.5px solid ${C.border}`,
                borderRadius: 10, padding: '12px 20px', fontSize: 13, fontWeight: 700,
                textDecoration: 'none', textAlign: 'center', fontFamily: FB, minWidth: 140 }}>
              📞 Call Hotel
            </a>
          )}
        </div>

        <div style={{ textAlign: 'center' }}>
          <button onClick={onReset}
            style={{ background: 'none', border: `1.5px solid ${C.border}`, borderRadius: 10,
              padding: '11px 28px', fontSize: 13, color: C.muted, cursor: 'pointer', fontFamily: FB,
              transition: 'border-color .2s' }}>
            Make Another Booking
          </button>
        </div>
      </div>

      {/* Footer */}
      <div style={{ background: C.navy, padding: '24px 32px', textAlign: 'center' }}>
        <p style={{ fontFamily: FD, fontSize: 16, color: 'rgba(255,255,255,.6)', fontWeight: 600 }}>{hotel.name}</p>
      </div>
    </div>
  );
}

/* ─── Main GuestBookingPage ───────────────────────────────────────────────── */
export default function GuestBookingPage() {
  const slug = new URLSearchParams(window.location.search).get('tenant') || 'dev';

  // view: 'hero' | 'results' | 'detail' | 'booking' | 'confirmation'
  const [view,          setView]         = useState('hero');
  const [hotel,         setHotel]        = useState(null);
  const [widget,        setWidget]       = useState(null);
  const [previewRooms,  setPreviewRooms] = useState([]);
  const [results,       setResults]      = useState([]);
  const [search,        setSearch]       = useState(null);
  const [detailRoom,    setDetailRoom]   = useState(null);
  const [selectedRoom,  setSelectedRoom] = useState(null);
  const [booking,       setBooking]      = useState(null);
  const [pageLoading,   setPageLoading]  = useState(true);
  const [searchLoading, setSearchLoading]= useState(false);
  const [submitLoading, setSubmitLoading]= useState(false);
  const [error,         setError]        = useState('');

  // Fetch hotel info + preview rooms on mount
  useEffect(() => {
    const defaultArrival   = addDays(today(), 1);
    const defaultDeparture = addDays(today(), 7);

    Promise.all([
      pubFetch(`/public/booking/${slug}/property`).catch(() => null),
      pubFetch(`/public/booking/${slug}/widget-config`).catch(() => null),
      pubFetch(`/public/booking/${slug}/availability?arrival=${defaultArrival}&departure=${defaultDeparture}&adults=2&children=0`).catch(() => []),
    ]).then(([h, w, rooms]) => {
      setHotel(h);
      setWidget(w);
      if (Array.isArray(rooms)) setPreviewRooms(rooms);
    }).finally(() => setPageLoading(false));
  }, [slug]);

  const handleSearch = useCallback(async (params) => {
    setSearchLoading(true);
    setError('');
    setSearch(params);
    try {
      const data = await pubFetch(
        `/public/booking/${slug}/availability?arrival=${params.arrival}&departure=${params.departure}&adults=${params.adults}&children=${params.children}`
      );
      const rooms = Array.isArray(data) ? data : [];
      setResults(rooms);
      setPreviewRooms(rooms);
      setView('results');
    } catch (e) {
      setError(e.message);
    } finally {
      setSearchLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const handleDetail = useCallback((room) => {
    setDetailRoom(room);
    setView('detail');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleBook = useCallback((room) => {
    if (!search) {
      // If no search done yet, scroll to search widget
      document.getElementById('search')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    setSelectedRoom(room);
    setView('booking');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [search]);

  const handleConfirm = useCallback(async (formData) => {
    setSubmitLoading(true);
    setError('');
    try {
      const data = await pubPost(`/public/booking/${slug}/reservations`, formData);
      setBooking(data);
      setView('confirmation');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitLoading(false);
    }
  }, [slug]);

  const handleReset = () => {
    setView('hero');
    setBooking(null);
    setSelectedRoom(null);
    setDetailRoom(null);
    setResults([]);
    setSearch(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ── Page loading ── */
  if (pageLoading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: C.cream, flexDirection: 'column', gap: 16, fontFamily: FB }}>
      <style>{GLOBAL_CSS}</style>
      <Spinner size={44} />
      <p style={{ color: C.muted, fontSize: 14, letterSpacing: '.05em' }}>Loading hotel information…</p>
    </div>
  );

  if (!hotel) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: C.cream, flexDirection: 'column', gap: 16, fontFamily: FB, textAlign: 'center', padding: 32 }}>
      <style>{GLOBAL_CSS}</style>
      <div style={{ fontSize: 64, opacity: .15 }}>🏨</div>
      <h2 style={{ fontFamily: FD, fontSize: 30, color: C.navy }}>Hotel Not Found</h2>
      <p style={{ color: C.muted, fontSize: 14, maxWidth: 400, lineHeight: 1.7 }}>
        The hotel "<strong>{slug}</strong>" could not be found. Please check the URL or contact the hotel directly.
      </p>
    </div>
  );

  return (
    <>
      <style>{GLOBAL_CSS}</style>

      {/* Global error banner */}
      {error && (
        <div style={{
          position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9000, background: C.dangerPale, border: `1px solid ${C.danger}30`,
          borderRadius: 10, padding: '12px 20px', fontSize: 13, color: C.danger,
          fontFamily: FB, display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 4px 20px rgba(184,48,37,.15)',
        }}>
          ⚠️ {error}
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.danger, fontSize: 18, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Global search loading overlay */}
      {searchLoading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(249,246,241,.88)', zIndex: 8000,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18 }}>
          <Spinner size={48} />
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontFamily: FD, fontSize: 22, color: C.navy, fontWeight: 600, marginBottom: 6 }}>Finding Available Rooms</p>
            <p style={{ fontSize: 13, color: C.muted, fontFamily: FB }}>Checking availability for your dates…</p>
          </div>
        </div>
      )}

      {/* Views */}
      {view === 'hero' && (
        <HotelHero
          hotel={hotel} widget={widget}
          previewRooms={previewRooms}
          searchLoading={searchLoading}
          search={search}
          onSearch={handleSearch}
          onDetail={handleDetail}
          onBook={handleBook}
        />
      )}

      {view === 'results' && (
        <SearchResults
          results={results} hotel={hotel} search={search}
          onDetail={handleDetail}
          onBook={room => { setSelectedRoom(room); setView('booking'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          onBack={() => setView('hero')}
        />
      )}

      {view === 'detail' && detailRoom && (
        <RoomDetailView
          room={detailRoom} hotel={hotel} search={search}
          onBook={room => {
            if (!search) {
              setView('hero');
              setTimeout(() => document.getElementById('search')?.scrollIntoView({ behavior: 'smooth' }), 100);
              return;
            }
            setSelectedRoom(room);
            setView('booking');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onBack={() => setView(results.length > 0 ? 'results' : 'hero')}
        />
      )}

      {view === 'booking' && selectedRoom && (
        <BookingForm
          room={selectedRoom} hotel={hotel} search={search}
          onConfirm={handleConfirm}
          onBack={() => setView(results.length > 0 ? 'results' : 'hero')}
          loading={submitLoading}
        />
      )}

      {view === 'confirmation' && booking && (
        <ConfirmationScreen booking={booking} hotel={hotel} onReset={handleReset} />
      )}
    </>
  );
}